import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import PublicIcon from "@mui/icons-material/Public";
import { Stack, Typography, Box } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { useHotkeys } from "react-hotkeys-hook";
import GoogleDriveIcon from "components/ui/icons/googleDrive.svg";
import {
  TopRightMenu,
  TopRightMenuItem,
} from "components/ui/molecules/TopRightMenu";
import CompanyWatcher from "models/company_watchers/CompanyWatcher";
import SlackLink from "components/ui/atoms/SlackLink";
import { useModal } from "@ebay/nice-modal-react";
import { useResetCompany } from "models/companies/apiCompany";
import { useContext } from "react";
import { UserContext } from "components/context/UserProvider";

const CompanyDomainLink = ({ domain }) => {
  return (
    <Box item>
      <Link href={`https://${domain}`} target="_blank" color="inherit">
        <Typography variant="caption">{domain}</Typography>
      </Link>
    </Box>
  );
};

const CompanyHeader = ({ company, toggleOpenSnapshotList }) => {
  const sourcingModal = useModal("SourcingDrawer");
  const dealModal = useModal("DealDrawer");
  const noteModal = useModal("NoteDrawer");
  const resetCompany = useResetCompany();
  const { user } = useContext(UserContext);

  const openCreateNote = () =>
    noteModal
      .show({
        formType: "create",
        initialValues: {
          owner: user.id,
          owner_detail: user,
          companies_ids: [company.id],
          companies_detail: [company],
          people: [],
          people_ids: [],
        },
      })
      .then(() => resetCompany(company.id));

  const openCreateDeal = () =>
    dealModal
      .show({
        formType: "create",
        initialValues: {
          company: company.id,
          company_detail: company,
        },
      })
      .then(() => resetCompany(company.id));

  const openCreateSourcing = () =>
    sourcingModal
      .show({
        formType: "create",
        initialValues: {
          company: company.id,
          company_detail: company,
        },
      })
      .then(() => resetCompany(company.id));

  useHotkeys("meta+s", () => openCreateSourcing, { preventDefault: true });
  useHotkeys("meta+d", () => openCreateDeal, { preventDefault: true });
  useHotkeys(
    "meta+h",
    () => {
      toggleOpenSnapshotList();
    },
    { preventDefault: true },
  );
  useHotkeys("meta+j", () => openCreateNote, { preventDefault: true });

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      width={1}
    >
      <Avatar src={`http://logo.clearbit.com/${company.best_domain}`} />
      <Stack direction="column">
        <Typography variant="subtitle2" noWrap>
          {company.best_name}
        </Typography>
        <Box
          columnSpacing={1}
          maxHeight="35px"
          overflow={"hidden"}
          fontSize={"small"}
        >
          {company.domains.map((domain, index) => (
            <CompanyDomainLink key={index} domain={domain} />
          ))}
        </Box>
      </Stack>
      <Box style={{ marginLeft: "auto" }}>
        <CompanyWatcher companyId={company.id} />
      </Box>
      <Box>
        <SlackLink slack_channel_id={company.slack_channel_id} />
      </Box>
      <TopRightMenu>
        <TopRightMenuItem
          alwaysShow
          iconButton
          endIcon={<PublicIcon />}
          href={company.website}
          target="_blank"
        >
          Website
        </TopRightMenuItem>
        {company?.link_drive && (
          <TopRightMenuItem
            alwaysShow
            iconButton
            endIcon={
              <Box>
                <img src={GoogleDriveIcon} />
              </Box>
            }
            href={`${company.link_drive}`}
            target="_blank"
          >
            Google
          </TopRightMenuItem>
        )}
        <TopRightMenuItem
          neverShow
          onClick={toggleOpenSnapshotList}
          endIcon={<HistoryIcon sx={{ scale: "0.8" }} />}
          title="&#8984;+h"
        >
          Deal history
        </TopRightMenuItem>
        <TopRightMenuItem
          neverShow
          onClick={openCreateDeal}
          endIcon={<AddIcon sx={{ scale: "0.8" }} />}
          title="&#8984;+d"
        >
          Deal
        </TopRightMenuItem>
        <TopRightMenuItem
          neverShow
          onClick={openCreateSourcing}
          endIcon={<AddIcon sx={{ scale: "0.8" }} />}
          title="&#8984;+s"
        >
          Sourcing
        </TopRightMenuItem>
        <TopRightMenuItem
          neverShow
          onClick={openCreateNote}
          endIcon={<AddIcon sx={{ scale: "0.8" }} />}
          title="&#8984;+j"
        >
          Note
        </TopRightMenuItem>
      </TopRightMenu>
    </Stack>
  );
};

export default CompanyHeader;
